import React, { FC } from 'react';
import { Button, Col, Row } from 'antd';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import PageHeader, { PageHeaderProps } from './PageHeader';
import DisplayModeButton, { DisplayModeButtonProps } from './DisplayModeButton';
import theme from '~/theme';

interface Props {
  headerComponent?: JSX.Element;
  header: PageHeaderProps;
  absoluteHeader?: boolean;
  filters?: JSX.Element;
  table?: JSX.Element;
  cards?: JSX.Element;
  map?: JSX.Element;
  add?: JSX.Element | null;
  className?: string;
  displayModeOptions?: DisplayModeButtonProps['options'];
  hideDisplayMode?: boolean; // don't affect if layout is mobile
  defaultDisplayMode?: DisplayModeButtonProps['mode'];
}

const PageList: FC<Props> = (props) => {
  const {
    headerComponent,
    header,
    absoluteHeader,
    filters,
    table,
    cards,
    map,
    displayModeOptions,
    defaultDisplayMode,
    hideDisplayMode,
    add,
    className,
  } = props;
  const [mode, changeMode] = DisplayModeButton.useDisplayMode(defaultDisplayMode);

  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  const { t } = useTranslation();
  return (
    <div className={`${className} PageList`}>
      {absoluteHeader && <div className="PageList-HeaderBlur" />}
      <div className="PageList-ControlPanelWrapper">
        <Row gutter={gutter}>
          <Col span={24}>{headerComponent || <PageHeader {...header} />}</Col>
        </Row>
        <Row gutter={gutter} className="PageList-Filters">
          {filters && <Col className="PageList-FiltersCol">{filters}</Col>}
          {!hideDisplayMode && !isMobile && (
            <Col>
              <DisplayModeButton mode={mode} changeMode={changeMode} options={displayModeOptions} />
            </Col>
          )}
          {add !== null && (
            <StyledCol flex="auto">{add || <Button type="primary">{t('add')}</Button>}</StyledCol>
          )}
        </Row>
      </div>

      <Row gutter={absoluteHeader ? undefined : gutter}>
        {(mode === 'table' || mode === 'cards') && (
          <Col span={24}>
            {mode === 'table' && table}
            {mode === 'cards' && cards}
          </Col>
        )}
        {mode === 'map' && map}
      </Row>
    </div>
  );
};

const gutter: [number, number] = [16, 16];

const StyledCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
`;

export default styled(PageList)`
  ${({ absoluteHeader }) =>
    absoluteHeader &&
    css`
      margin: -16px -24px;
      .PageList-HeaderBlur {
        position: absolute;
        pointer-events: none;
        height: 230px;
        width: calc(100vw - 200px);
        z-index: 2;
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0.75) 0%,
          rgba(255, 255, 255, 0.45) 60%,
          rgba(255, 255, 255, 0.2) 75%,
          rgba(255, 255, 255, 0) 100%
        );
      }
      .PageList-ControlPanelWrapper {
        // pointer-events: none;
        padding: 16px 24px;
        position: absolute;
        z-index: 4;
        width: calc(100vw - 200px);
      }
    `}
  .PageList-ControlPanelWrapper {
    margin-bottom: 16px;
    @media (max-width: ${theme.breakpoints.sm}px) {
      margin-bottom: 32px;
    }
    .PageList-Filters {
      .PageList-FiltersCol {
        padding-bottom: 0 !important;
        .ant-row {
          margin-bottom: 8px;
        }
      }
      .ant-form-item-control,
      .ant-radio-group {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
      }
    }
  }
  button,
  .PageHeader-Input,
  .ant-pagination-item,
  .ant-table {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  }
  .ant-btn-primary {
    box-shadow: 0 2px 6px rgba(139, 96, 247, 0.3);
  }
  .ant-list-pagination {
    margin-top: 0;
  }
`;
